import Plugin from '@ckeditor/ckeditor5-core/src/plugin';
import ButtonView from '@ckeditor/ckeditor5-ui/src/button/buttonview';
import Widget from '@ckeditor/ckeditor5-widget/src/widget';
import { toWidget } from '@ckeditor/ckeditor5-widget/src/utils';

const VIDEO_ICON = '<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20"><path d="M3 5a2 2 0 0 1 2-2h7a2 2 0 0 1 2 2v1.382l2.553-1.276A1 1 0 0 1 18 6v8a1 1 0 0 1-1.447.894L14 13.618V15a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V5zm2-1a1 1 0 0 0-1 1v10a1 1 0 0 0 1 1h7a1 1 0 0 0 1-1V5a1 1 0 0 0-1-1H5zm9 3.236v5.528l2 1V7.236l-2 1z"/></svg>';

const VIDEO_MIME_TYPES = ['video/mp4', 'video/webm', 'video/ogg'];

// ── 스켈레톤 내부 HTML ─────────────────────────────────────────────────────
// 빈 <video> 태그를 기반으로 실제 비디오와 동일한 크기를 유지하고,
// 그 위에 로딩 overlay를 얹는 방식
function buildSkeletonDom(domElement) {
	domElement.style.cssText = 'pointer-events:none;display:block;';
	domElement.innerHTML = `
		<style>
			@keyframes ck-spin{from{transform:rotate(0deg)}to{transform:rotate(360deg)}}
			.ck-spinner{transform-origin:12px 12px;animation:ck-spin 1s linear infinite;}
		</style>
		<div style="width:100%;aspect-ratio:16/9;background:#000;border-radius:4px;overflow:hidden;position:relative;box-sizing:border-box;">
			<!-- 중앙 스피너 + % -->
			<div style="position:absolute;top:50%;left:50%;transform:translate(-50%,-60%);display:flex;flex-direction:column;align-items:center;gap:12px;">
				<svg width="40" height="40" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
					<circle class="ck-spinner" cx="12" cy="12" r="9" fill="none" stroke="rgba(255,255,255,0.7)" stroke-width="2" stroke-dasharray="45 15"/>
				</svg>
				<span data-progress-text style="color:rgba(255,255,255,0.7);font-size:13px;font-family:sans-serif;letter-spacing:0.5px;">0%</span>
			</div>
			<!-- 하단 컨트롤바 -->
			<div style="position:absolute;bottom:0;left:0;right:0;">
				<!-- 씨크바 -->
				<div style="width:100%;height:4px;background:#111;">
					<div style="width:0%;height:100%;background:#aaa;"></div>
				</div>
				<!-- 컨트롤 -->
				<div style="background:rgba(0,0,0,0.85);padding:5px 10px;display:flex;align-items:center;gap:8px;box-sizing:border-box;">
					<!-- 재생 -->
					<svg width="18" height="18" viewBox="0 0 24 24" fill="rgba(255,255,255,0.85)" xmlns="http://www.w3.org/2000/svg">
						<path d="M8 5v14l11-7z"/>
					</svg>
					<!-- 시간 -->
					<span style="color:rgba(255,255,255,0.85);font-size:12px;font-family:sans-serif;white-space:nowrap;">0:00 / 0:00</span>
					<div style="flex:1;"></div>
					<!-- 볼륨 -->
					<svg width="18" height="18" viewBox="0 0 24 24" fill="rgba(255,255,255,0.85)" xmlns="http://www.w3.org/2000/svg">
						<path d="M3 9v6h4l5 5V4L7 9H3zm13.5 3A4.5 4.5 0 0 0 14 7.97v8.05c1.48-.73 2.5-2.25 2.5-4.02z"/>
					</svg>
					<!-- 전체화면 -->
					<svg width="18" height="18" viewBox="0 0 24 24" fill="rgba(255,255,255,0.85)" xmlns="http://www.w3.org/2000/svg">
						<path d="M7 14H5v5h5v-2H7v-3zm-2-4h2V7h3V5H5v5zm12 7h-3v2h5v-5h-2v3zM14 5v2h3v3h2V5h-5z"/>
					</svg>
					<!-- 더보기 -->
					<svg width="18" height="18" viewBox="0 0 24 24" fill="rgba(255,255,255,0.85)" xmlns="http://www.w3.org/2000/svg">
						<path d="M12 8c1.1 0 2-.9 2-2s-.9-2-2-2-2 .9-2 2 .9 2 2 2zm0 2c-1.1 0-2 .9-2 2s.9 2 2 2 2-.9 2-2-.9-2-2-2zm0 6c-1.1 0-2 .9-2 2s.9 2 2 2 2-.9 2-2-.9-2-2-2z"/>
					</svg>
				</div>
			</div>
		</div>
	`;
}

class VideoUpload extends Plugin {

	static get requires() {
		return [Widget];
	}

	init() {
		const editor = this.editor;

		// ── 스키마 등록 ────────────────────────────────────
		editor.model.schema.register('videoUploadPlaceholder', {
			isObject: true,
			allowWhere: '$block',
		});

		editor.model.schema.register('videoBlock', {
			isObject: true,
			allowWhere: '$block',
			allowAttributes: ['src'],
		});

		// ── videoUploadPlaceholder Editing downcast ────────
		editor.conversion.for('editingDowncast').elementToElement({
			model: 'videoUploadPlaceholder',
			view: (modelElement, { writer }) => {
				const container = writer.createContainerElement('div', {
					class: 'ck-video-upload-placeholder',
					style: 'display:block;padding:1rem 0;line-height:0;border:2px solid transparent;box-sizing:border-box;',
				});
				const inner = writer.createRawElement('div', {}, buildSkeletonDom);
				writer.insert(writer.createPositionAt(container, 0), inner);
				return toWidget(container, writer, { label: '동영상 업로드 중' });
			},
		});

		// ── videoUploadPlaceholder Data downcast ───────────
		editor.conversion.for('dataDowncast').elementToElement({
			model: 'videoUploadPlaceholder',
			view: (modelElement, { writer }) => writer.createEmptyElement('span'),
		});

		// ── videoBlock Editing downcast (block widget) ─────
		editor.conversion.for('editingDowncast').elementToElement({
			model: 'videoBlock',
			view: (modelElement, { writer }) => {
				const src = modelElement.getAttribute('src') || '';
				const container = writer.createContainerElement('div', {
					class: 'ck-video-block',
					style: 'margin:1rem 0;line-height:0;',
				});
				const videoEl = writer.createEmptyElement('video', {
					controls: 'controls',
					src,
					preload: 'metadata',
					style: 'max-width:100%;display:block;border-radius:4px;width:100%;',
				});
				writer.insert(writer.createPositionAt(container, 0), videoEl);
				return toWidget(container, writer, { label: '동영상' });
			},
		});

		// ── videoBlock Data downcast ───────────────────────
		editor.conversion.for('dataDowncast').elementToElement({
			model: 'videoBlock',
			view: (modelElement, { writer }) => {
				const src = modelElement.getAttribute('src') || '';
				return writer.createEmptyElement('video', {
					controls: 'controls',
					src,
					preload: 'metadata',
					style: 'max-width:100%;display:block;margin:1rem 0;border-radius:4px;',
				});
			},
		});

		// ── videoBlock Upcast: <video src> → videoBlock ────
		editor.conversion.for('upcast').elementToElement({
			view: { name: 'video' },
			model: (viewElement, { writer }) => {
				const src = viewElement.getAttribute('src') || '';
				return writer.createElement('videoBlock', { src });
			},
			converterPriority: 'high',
		});

		// ── 툴바 버튼 ──────────────────────────────────────
		editor.ui.componentFactory.add('videoUpload', locale => {
			const button = new ButtonView(locale);
			button.set({ label: '동영상 업로드', icon: VIDEO_ICON, tooltip: true, withText: false });

			button.on('execute', () => {
				const config = editor.config.get('videoUpload') || {};
				const input = document.createElement('input');
				input.type = 'file';
				input.accept = 'video/mp4,video/webm,video/ogg';
				input.style.display = 'none';
				document.body.appendChild(input);

				input.onchange = async () => {
					const file = input.files[0];
					document.body.removeChild(input);
					if (!file) return;
					button.set('isEnabled', false);
					try { await this._uploadAndInsert(file, config); }
					finally { button.set('isEnabled', true); }
				};
				input.click();
			});

			return button;
		});

		// ── 드래그앤드롭 ────────────────────────────────────
		editor.editing.view.document.on('drop', (evt, data) => {
			const files = data.dataTransfer.files;
			if (!files || files.length === 0) return;
			const videoFiles = Array.from(files).filter(f => VIDEO_MIME_TYPES.includes(f.type));
			if (videoFiles.length === 0) return;

			// evt.stop() 제거 — CKEditor 기본 drop 처리가 실행되어
			// ck-clipboard-drop-target-position 마커 클린업 + selection 설정이 이루어짐
			data.preventDefault();

			const config = editor.config.get('videoUpload') || {};

			// CKEditor 기본 처리(마커 클린업, dropRange selection 설정) 완료 후 다음 프레임에 업로드 시작
			requestAnimationFrame(() => {
				videoFiles.forEach(file => {
					this._uploadAndInsert(file, config).catch(err => {
						console.error('[VideoUpload] 드래그앤드롭 업로드 실패:', err);
					});
				});
			});
		}, { priority: 'high' });
	}

	// ── widget DOM에서 progress 직접 업데이트 ───────────────
	_updateWidgetProgress(placeholderElement, percent) {
		const editor = this.editor;
		try {
			const viewEl = editor.editing.mapper.toViewElement(placeholderElement);
			if (!viewEl) return;
			const domEl = editor.editing.view.domConverter.mapViewToDom(viewEl);
			if (!domEl) return;
			const bar = domEl.querySelector('[data-progress-bar]');
			if (bar) bar.style.width = `${percent}%`;
			const text = domEl.querySelector('[data-progress-text]');
			if (text) text.textContent = `${percent}%`;
		} catch (e) {}
	}

	// ── 공통 업로드 + 삽입 ────────────────────────────────
	_uploadAndInsert(file, config) {
		const editor = this.editor;
		const uploadUrl       = config.uploadUrl       || '/video';
		const onUploadSuccess = config.onUploadSuccess || null;

		let placeholderElement = null;
		let aborted = false;
		let xhr = null;

		// ── placeholder(widget) 삭제 감지 → XHR 취소 ──────
		const deleteWatcher = () => {
			if (!placeholderElement || aborted) return;
			if (placeholderElement.root && placeholderElement.root.rootName === '$graveyard') {
				aborted = true;
				if (xhr) xhr.abort();
				this.stopListening(editor.model.document, 'change', deleteWatcher);
			}
		};

		// 1. widget placeholder 삽입 + widget 자체 선택 (caret 제거)
		editor.model.change(writer => {
			const insertPosition = editor.model.document.selection.getFirstPosition();
			placeholderElement = writer.createElement('videoUploadPlaceholder');
			writer.insert(placeholderElement, insertPosition);
			writer.setSelection(placeholderElement, 'on');
		});

		// drop 이벤트 중 selection 변화 무시 → 다음 프레임부터 감지
		requestAnimationFrame(() => {
			this.listenTo(editor.model.document, 'change', deleteWatcher);
		});

		// 2. XHR 업로드
		return new Promise((resolve) => {
			const formData = new FormData();
			formData.append('video', file);

			xhr = new XMLHttpRequest();
			xhr.open('POST', uploadUrl, true);
			xhr.withCredentials = true;

			xhr.upload.onprogress = (e) => {
				if (aborted) return;
				if (e.lengthComputable) {
					const percent = Math.min(Math.round((e.loaded / e.total) * 100), 99);
					this._updateWidgetProgress(placeholderElement, percent);
				}
			};

			xhr.onload = () => {
				if (aborted) { resolve(); return; }
				this.stopListening(editor.model.document, 'change', deleteWatcher);

				try {
					const data = JSON.parse(xhr.responseText);

					if (data.id && data.fileName) {
						this._updateWidgetProgress(placeholderElement, 100);

						const fileName = data.fileName;

						// 스켈레톤 fade-out
						requestAnimationFrame(() => {
							try {
								const viewEl = editor.editing.mapper.toViewElement(placeholderElement);
								const domEl = viewEl && editor.editing.view.domConverter.mapViewToDom(viewEl);
								if (domEl) {
									domEl.style.transition = 'opacity 0.3s ease';
									domEl.style.opacity = '0';
								}
							} catch (e) {}

							// fade-out 후 video 삽입 (CSS animation으로 fade-in)
							setTimeout(() => {
								editor.model.change(writer => {
									if (!placeholderElement || placeholderElement.root.rootName === '$graveyard') return;
									const videoPosition = writer.createPositionBefore(placeholderElement);
									const viewFragment = editor.data.processor.toView(
										`<video controls src="${fileName}" preload="metadata" style="max-width:100%;display:block;margin:1rem 0;border-radius:4px;"></video>`
									);
									const modelFragment = editor.data.toModel(viewFragment);
									editor.model.insertContent(modelFragment, videoPosition);
									writer.remove(placeholderElement);
								});

								// 빈 paragraph 제거
								requestAnimationFrame(() => {
									requestAnimationFrame(() => {
										const domRoot = editor.editing.view.getDomRoot();
										let videoEl = null;
										try {
											videoEl = domRoot.querySelector(`video[src="${CSS.escape(fileName)}"]`);
										} catch(e) {
											videoEl = domRoot.querySelector('video');
										}
										if (!videoEl) { if (onUploadSuccess) onUploadSuccess(data.id, fileName); return; }

										let videoPara = videoEl.parentElement;
										while (videoPara && videoPara !== domRoot && videoPara.tagName !== 'P') {
											videoPara = videoPara.parentElement;
										}
										const nextEl = videoPara && videoPara.tagName === 'P' ? videoPara.nextElementSibling : null;
										if (nextEl && nextEl.tagName === 'P' &&
											(nextEl.textContent.trim() === '' || nextEl.textContent === '\u00a0')) {
											try {
												const vEl = editor.editing.view.domConverter.mapDomToView(nextEl);
												const mEl = vEl && editor.editing.mapper.toModelElement(vEl);
												if (mEl) editor.model.change(w => w.remove(mEl));
											} catch(e) {}
										}

										if (onUploadSuccess) onUploadSuccess(data.id, fileName);
									});
								});
							}, 300);
						});
					} else {
						console.error('[VideoUpload] 업로드 응답 오류:', data);
						if (placeholderElement && placeholderElement.root.rootName !== '$graveyard') {
							editor.model.change(writer => writer.remove(placeholderElement));
						}
					}
				} catch (err) {
					console.error('[VideoUpload] 응답 파싱 실패:', err);
					if (placeholderElement && placeholderElement.root.rootName !== '$graveyard') {
						editor.model.change(writer => writer.remove(placeholderElement));
					}
				} finally {
					resolve();
				}
			};

			xhr.onabort = () => {
				this.stopListening(editor.model.document, 'change', deleteWatcher);
				resolve();
			};

			xhr.onerror = () => {
				this.stopListening(editor.model.document, 'change', deleteWatcher);
				if (placeholderElement && placeholderElement.root.rootName !== '$graveyard') {
					editor.model.change(writer => writer.remove(placeholderElement));
				}
				console.error('[VideoUpload] 네트워크 오류');
				resolve();
			};

			xhr.send(formData);
		});
	}
}

export default VideoUpload;
